import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { sectors } from "data/performanceKpiData";
import { ROLES } from "context/WorkersContext";

// Default operator KPIs (used for evaluations)
const defaultOperatorKpis = [
  { id: "kpi-1", name: "Moisture Content (%)", definition: "Percentage of water in raw cashew nuts; ideal range is 8-10% to prevent fungal growth.", iconColor: "text-blue-500" },
  { id: "kpi-2", name: "Kernel Integrity", definition: "Ratio of whole kernels vs. brokens after initial processing and test-shelling.", iconColor: "text-emerald-500" },
  { id: "kpi-3", name: "Grading Accuracy", definition: "Precision in categorizing nuts by size and quality grades (e.g., WW240, WW320).", iconColor: "text-purple-500" },
  { id: "kpi-4", name: "Daily Throughput", definition: "Total tonnage of raw nuts processed and cleared through the receiving station per shift.", iconColor: "text-amber-500" },
  { id: "kpi-5", name: "Safety Protocol Adherence", definition: "Compliance with factory floor safety standards and PPE usage in the receiving zone.", iconColor: "text-orange-500" },
];

// Default supervisor KPIs (team oversight, reporting, etc.)
const defaultSupervisorKpis = [
  { id: "kpi-s1", name: "Team Performance", definition: "Overall team output and adherence to targets.", iconColor: "text-blue-500" },
  { id: "kpi-s2", name: "Safety Compliance", definition: "Ensuring team follows safety protocols and PPE usage.", iconColor: "text-orange-500" },
  { id: "kpi-s3", name: "Quality Oversight", definition: "Monitoring product quality and addressing issues promptly.", iconColor: "text-emerald-500" },
];

const buildInitialState = () => {
  const state = {};
  sectors.forEach((s) => {
    state[s.id] = {
      [ROLES.OPERATOR]: [...defaultOperatorKpis],
      [ROLES.SUPERVISOR]: [...defaultSupervisorKpis],
    };
  });
  return state;
};

const SectorKpiContext = createContext(null);

export function SectorKpiProvider({ children }) {
  const [sectorRoleKpis, setSectorRoleKpis] = useState(buildInitialState);

  const getKpisForSectorRole = useCallback(
    (sectorId, role) => {
      const sector = sectorRoleKpis[sectorId];
      if (!sector) return [];
      return sector[role] || [];
    },
    [sectorRoleKpis]
  );

  const addKpi = useCallback((sectorId, role, kpi) => {
    const id = kpi.id != null ? String(kpi.id) : `kpi-${Date.now()}`;
    setSectorRoleKpis((prev) => ({
      ...prev,
      [sectorId]: {
        ...(prev[sectorId] || { [ROLES.OPERATOR]: [], [ROLES.SUPERVISOR]: [] }),
        [role]: [...(prev[sectorId]?.[role] || []), { ...kpi, id }],
      },
    }));
    return id;
  }, []);

  const updateKpi = useCallback((sectorId, role, kpiId, updates) => {
    setSectorRoleKpis((prev) => ({
      ...prev,
      [sectorId]: {
        ...(prev[sectorId] || {}),
        [role]: (prev[sectorId]?.[role] || []).map((k) =>
          k.id === kpiId ? { ...k, ...updates } : k
        ),
      },
    }));
  }, []);

  const removeKpi = useCallback((sectorId, role, kpiId) => {
    setSectorRoleKpis((prev) => ({
      ...prev,
      [sectorId]: {
        ...(prev[sectorId] || {}),
        [role]: (prev[sectorId]?.[role] || []).filter((k) => k.id !== kpiId),
      },
    }));
  }, []);

  const initializeSector = useCallback((sectorId) => {
    setSectorRoleKpis((prev) => {
      if (prev[sectorId]) return prev;
      return {
        ...prev,
        [sectorId]: {
          [ROLES.OPERATOR]: [...defaultOperatorKpis],
          [ROLES.SUPERVISOR]: [...defaultSupervisorKpis],
        },
      };
    });
  }, []);

  const value = {
    sectorRoleKpis,
    getKpisForSectorRole,
    addKpi,
    updateKpi,
    removeKpi,
    initializeSector,
  };

  return (
    <SectorKpiContext.Provider value={value}>
      {children}
    </SectorKpiContext.Provider>
  );
}

export function useSectorKpis() {
  const ctx = useContext(SectorKpiContext);
  if (!ctx) throw new Error("useSectorKpis must be used within SectorKpiProvider");
  return ctx;
}

export default SectorKpiContext;
